import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'react-feather';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useDispatch } from 'react-redux';
import { setTempPhoneNumber } from '../../store/slices/authSlice';
import { setupRecaptcha, sendVerificationCode,logoutUser } from '../../services/auth.service';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import 'react-phone-number-input/style.css';
import './PhoneLogin.css';
import Spinner from '../../components/common/Spinner';

const PhoneLogin = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const mountedRef = useRef(false);

    // Gérer l'état de montage du composant
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Vérifier si l'utilisateur est déjà connecté
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user && mountedRef.current) {
                console.log('Utilisateur déjà connecté, rediriger vers la page d\'accueil', user);
                logoutUser();
                // Utilisateur déjà connecté, rediriger vers la page d'accueil
                navigate('/home');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Initialiser le reCAPTCHA invisible au premier rendu
    useEffect(() => {
        try {
            setupRecaptcha('recaptcha-container');
        } catch (err) {
            console.error("Erreur d'initialisation reCAPTCHA:", err);
            if(mountedRef.current) {
                setError(t('login.errors.recaptchaInit'));
            }
        }
    }, [t]);

    const handleSubmit = async () => {
        if (!mountedRef.current) return;
        
        setLoading(true);
        setError('');

        try {
            // Validation du numéro de téléphone
            if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
                setError(t('login.errors.invalidPhone'));
                setLoading(false);
                return;
            }

            // Sauvegarder le numéro temporairement
            dispatch(setTempPhoneNumber(phoneNumber));

            // Envoyer le code de vérification via Firebase
            const confirmationResult = await sendVerificationCode(phoneNumber);
            
            if (confirmationResult) {
                // Succès : naviguer vers la page de vérification OTP
                navigate('/auth/otp');
            }

        } catch (error) {
            console.error('Erreur lors de l\'envoi du code SMS:', error);
            
            // Gestion des erreurs spécifiques Firebase
            if (error.code === 'auth/invalid-phone-number') {
                setError(t('login.errors.invalidPhone'));
            } else if (error.code === 'auth/operation-not-allowed') {
                setError(t('login.errors.regionNotAllowed'));
            } else if (error.code === 'auth/too-many-requests') {
                setError(t('login.errors.tooManyRequests'));
            } else if (error.code === 'auth/captcha-check-failed') {
                setError(t('login.errors.recaptchaInit'));
            } else if (error.code === 'auth/missing-phone-number') {
                setError(t('login.errors.invalidPhone'));
            } else if (error.code === 'auth/quota-exceeded') {
                setError('Quota d\'envoi de SMS dépassé. Veuillez réessayer plus tard.');
            } else if (error.code === 'auth/unregistered-instance-id') {
                setError('Erreur de configuration. Veuillez contacter le support.');
            } else {
                setError(t('login.errors.general'));
            }

            // Nettoyer les données temporaires en cas d'erreur
            dispatch(setTempPhoneNumber(''));
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    };

    return (
        <div className="login-container">
            {loading && <Spinner />}
            <div className="page-content">
                <div className="container">
                    <div className="account-area style-2">
                        {/* Retour à l'écran précédent (historique du navigateur) plutôt qu'une route fixe */}
                        <a href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }} className="back-btn">
                            <ChevronLeft className="icon" />
                        </a>
                        <div className="section-head ps-0">
                            <h2>{t('login.title')}</h2>
                            <p>{t('login.subtitle')}</p>
                        </div>

                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        <div className="dz-contact">
                            <PhoneInput
                                international
                                countryCallingCodeEditable={false}
                                defaultCountry="CI"
                                value={phoneNumber}
                                onChange={setPhoneNumber}
                                className="phone-input-container"
                                labels={t('login.phoneLabels', { returnObjects: true })}
                            />
                        </div>
                        {/* Ce conteneur est requis par Firebase et sera invisible. */}
                        <div id="recaptcha-container"></div>
                    </div>
                </div>
            </div>
            <div className="footer fixed bg-white p-b15">
                <div className="container">
                    <button
                        className="btn btn-lg btn-gradient w-100 dz-flex-box btn-shadow rounded-xl"
                        onClick={handleSubmit}
                        disabled={loading || !phoneNumber}
                    >
                           {loading ? t('login.sending') : t('login.continue')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PhoneLogin;
